import * as React from "react";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QuickAnswer } from "@/components/seo/QuickAnswer";
import { FAQSection } from "@/components/seo/FAQSection";
import { useScrollToTop } from "@/hooks/useScrollToTop";

const priceTable: { model: string; from: number; to: string; path: string; bestFor: string }[] = [
  { model: "VENA", from: 600, to: "Integrated bidet focus", path: "/products/vena", bestFor: "Bidet-first replacement of a separate fixture" },
  { model: "L1", from: 600, to: "Compact entry", path: "/products/l1", bestFor: "Small bathrooms and apartment schemes" },
  { model: "AURELIUS", from: 600, to: "Classical finish", path: "/products/aurelius", bestFor: "Traditional and heritage interiors" },
  { model: "REM7", from: 600, to: "Flagship reference model", path: "/products/rem7", bestFor: "Project standard across mixed unit types" },
  { model: "LUNA", from: 600, to: "Wellness comfort", path: "/products/luna", bestFor: "Spa-oriented private bathrooms" },
  { model: "M3", from: 600, to: "Volume specification", path: "/products/m3", bestFor: "High-volume residential developments" },
  { model: "LUMA", from: 600, to: "Contemporary minimal", path: "/products/luma", bestFor: "Design-led minimalist bathrooms" },
  { model: "U1", from: 600, to: "Wall-hung variant", path: "/products/u1", bestFor: "Wall-hung retrofits with existing carrier" },
  { model: "ARIA", from: 600, to: "Eco wall-hung", path: "/products/aria", bestFor: "Compact and space-recovery layouts" },
  { model: "K2", from: 600, to: "Premium variant", path: "/products/k2", bestFor: "Upper-tier residential specification" },
  { model: "LUXA", from: 600, to: "Premium flagship", path: "/products/luxa", bestFor: "Signature suites and show apartments" },
  { model: "S1", from: 600, to: "Advanced automation", path: "/products/s1", bestFor: "Fully automated premium bathrooms" },
  { model: "NOVA", from: 600, to: "Family / multi-user", path: "/products/nova", bestFor: "Multi-user households and family suites" },
  { model: "AURELUX", from: 600, to: "Ultra-luxury", path: "/products/aurelux", bestFor: "Gold-accented statement installations" },
];

const faqs = [
  {
    question: "How much does a smart toilet cost in Europe?",
    answer:
      "Special offer €600 per unit. Installation is additional and typically depends on whether power and waste already exist in the right position.",
  },
  {
    question: "Why do smart toilets vary so much in price?",
    answer:
      "Four factors drive most of the spread: whether the bidet is fully integrated or a seat add-on, whether heating is instant tankless or reservoir-based, whether UV-C sterilization and automation are included, and the ceramic and finish quality. Wall-hung and tankless engineering also costs more than a floor-standing cistern unit.",
  },
  {
    question: "What does installation cost in Europe?",
    answer:
      "As a planning figure, a straightforward like-for-like swap where a fused power point already exists is the cheapest scenario. Adding a dedicated RCD-protected electrical point, moving the waste, or building a wall-hung carrier frame each add materially to the labour. Get a plumber and an electrician to survey together rather than sequentially — that single step avoids most cost surprises.",
  },
  {
    question: "Is a more expensive smart toilet worth it?",
    answer:
      "It depends on where the unit sits. In a signature suite or a show apartment the finish carries commercial weight. In a standard room or a rental unit, a mid-tier model with the same core hygiene functions delivers most of the guest-facing benefit at a lower cost per key.",
  },
  {
    question: "Do you offer bulk or developer pricing?",
    answer:
      "Yes. Volume tiers are quoted directly for developers, hotels and architects, with 10–15 day air freight on stocked SKUs and roughly 240 units per 40-ft container on the 40-day sea route. Request a project quotation through the B2B page.",
  },
  {
    question: "How does this compare with other brands?",
    answer:
      "As context rather than a head-to-head claim: TOTO's European flagship range sits considerably above this band, while Kohler's entry tier starts below it. Comparing like for like means matching the specification — integrated bidet, tankless heating, wall-hung installation — before comparing the number.",
  },
];

const SmartToiletCost = () => {
  useScrollToTop();
  const { language } = useI18n();
  const seo = getSeoCopy("smartToiletCost", language);

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Smart Toilets", url: "/smart-toilets" },
    { name: "Smart Toilet Cost Guide", url: "/smart-toilet-cost" },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "How Much Does a Smart Toilet Cost? A 2026 European Price Guide",
    description:
      "Smart toilet prices in Europe by tier, what drives the cost, installation budgets, the full Aureum VII price table and developer bulk pricing.",
    author: { "@type": "Organization", name: "Aureum VII Trading" },
    publisher: {
      "@type": "Organization",
      name: "Aureum VII Trading",
      logo: { "@type": "ImageObject", url: "https://www.aureum7trading.com/images/og-default.jpg" },
    },
    mainEntityOfPage: "https://www.aureum7trading.com/en/smart-toilet-cost",
    datePublished: "2026-08-17",
    dateModified: "2026-08-17",
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead
        route="smartToiletCost"
        schema={[structuredData, {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": b.name,
            "item": `https://www.aureum7trading.com${b.url}`
          }))
        }]}
      />
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative pt-28 pb-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-4xl text-center">
            <Badge variant="outline" className="border-accent/30 text-accent bg-accent/10 mb-6">
              Buying Guide
            </Badge>
            <h1 className="font-playfair text-4xl sm:text-5xl font-light mb-6 tracking-tight text-primary">
              {seo.h1}
            </h1>
            <p className="font-inter text-lg text-muted-foreground leading-relaxed">
              A transparent 2026 price guide for European buyers — by tier, by model, and
              with the installation costs most brochures leave out.
            </p>
          </div>
        </section>

        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-12 py-10">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Smart Toilets", href: "/smart-toilets" },
              { label: "Smart Toilet Cost" },
            ]}
          />

          <QuickAnswer question="Smart toilet price in Europe, 2026">
            Aureum VII Trading: <strong>Special offer €600 per unit</strong>. Installation is separate and depends
            mainly on whether a dedicated power point and the right waste position
            already exist. Volume pricing for developers and hotels is quoted directly.
          </QuickAnswer>

          <article className="prose prose-neutral max-w-none space-y-6">
            <h2 className="text-2xl font-semibold mt-10 mb-4">Unit Offer by Tier</h2>
            <p>
              The Aureum VII Trading unit offer is €600 per unit across the collection.
              Compare the specification tiers below; competitor pricing requires a dealer quotation.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-3 text-left">Tier</th>
                    <th className="p-3 text-left">Aureum VII unit offer</th>
                    <th className="p-3 text-left">What you get</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium">Entry integrated</td>
                    <td className="p-3">Special offer €600 per unit</td>
                    <td className="p-3">Integrated bidet, heated seat, warm-air drying, soft-close, eco flush</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium">Mid / core specification</td>
                    <td className="p-3">Special offer €600 per unit</td>
                    <td className="p-3">Tankless instant heating, UV-C nozzle sterilization, motion sensors, user presets, wall-hung options</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium">Premium &amp; ultra-luxury</td>
                    <td className="p-3">Special offer €600 per unit</td>
                    <td className="p-3">Full automation, premium ceramics and finishes, statement design, extended feature sets</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground">
              For market context only, not a head-to-head claim: TOTO's European flagship
              range sits considerably above this band, while Kohler's entry tier starts
              below it. Specification, not brand, is what makes a price comparable.
            </p>

            <h2 className="text-2xl font-semibold mt-10 mb-4">What Actually Drives the Price</h2>
            <h3 className="text-xl font-semibold mt-6 mb-2">1. Bidet integration</h3>
            <p>
              A cleansing seat bolted onto an existing pan is the cheapest route into the
              category. A fully integrated unit — where the bowl, nozzle, heating and
              controls are engineered as one product — costs more because the ceramic,
              the plumbing path and the electronics are designed together. It also cleans
              better and looks like a single object rather than an accessory.
            </p>
            <h3 className="text-xl font-semibold mt-6 mb-2">2. Water heating method</h3>
            <p>
              Reservoir heating stores a fixed volume of warm water and runs cool once it
              is used. <strong>Tankless instant heating</strong> warms water on demand,
              which means unlimited warm cleansing and no standing reservoir to maintain.
              It is one of the clearest cost steps between tiers, and the one guests
              notice most.
            </p>
            <h3 className="text-xl font-semibold mt-6 mb-2">3. UV-C sterilization and self-cleaning</h3>
            <p>
              UV-C sterilization of the nozzle between uses, plus self-cleaning nozzle
              cycles, add both component cost and long-term maintenance value. In
              hospitality this is usually the feature that justifies the step up, because
              it reduces the burden on housekeeping.
            </p>
            <h3 className="text-xl font-semibold mt-6 mb-2">4. Automation and sensors</h3>
            <p>
              Motion-sensing lid opening, auto flush, user presets, night lighting and
              remote or panel control each add electronics and testing. They are also the
              features most responsible for the perceived luxury of the product.
            </p>
            <h3 className="text-xl font-semibold mt-6 mb-2">5. Installation complexity</h3>
            <p>
              This is the line most budgets miss. A wall-hung unit needs a carrier frame
              in the wall; a tankless unit needs adequate supply pressure; every unit
              needs a dedicated, RCD-protected power point sited outside the wet zone.
              None of that is exotic work, but it is real work.
            </p>

            <h2 className="text-2xl font-semibold mt-10 mb-4">Installation Costs in Europe</h2>
            <p>
              Installation is quoted locally, so treat the following as a structure for
              your own quotation rather than a price list:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Like-for-like swap, power already present.</strong> The cheapest case — a plumber's half-day in most markets.</li>
              <li><strong>New electrical point.</strong> An electrician's visit plus making good. Usually the largest single adder in a retrofit.</li>
              <li><strong>Wall-hung conversion.</strong> Carrier frame, wall build-up, re-tiling. Plan this as a bathroom works package, not a fixture swap.</li>
              <li><strong>Pressure remediation.</strong> Only needed where a survey shows the riser cannot deliver consistent inlet pressure — but far cheaper to discover before ordering.</li>
              <li><strong>Multi-unit projects.</strong> Cost per unit falls sharply once a trade team repeats the same install, which is why a mock-up room pays for itself.</li>
            </ul>
            <p>
              A practical rule: survey the plumbing and the electrics in the same visit.
              Sequential surveys are how retrofit budgets drift.
            </p>

            <h2 className="text-2xl font-semibold mt-10 mb-4">Aureum VII Price Table (Every Model)</h2>
            <p>
              Each price below is the published unit offer shown on that
              model's own page.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-3 text-left">Model</th>
                    <th className="p-3 text-left">Unit offer</th>
                    <th className="p-3 text-left">Positioning</th>
                    <th className="p-3 text-left">Best for</th>
                  </tr>
                </thead>
                <tbody>
                  {priceTable.map((row) => (
                    <tr key={row.model} className="border-t border-border">
                      <td className="p-3 font-medium">
                        <LocalizedLink to={row.path} className="text-primary underline underline-offset-4">
                          {row.model}
                        </LocalizedLink>
                      </td>
                      <td className="p-3">Special offer €{row.from.toLocaleString("en-US")} per unit</td>
                      <td className="p-3">{row.to}</td>
                      <td className="p-3">{row.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground">
              Prices are per unit and match the structured data on each product page.
              Volume pricing is quoted separately.
            </p>

            <h2 className="text-2xl font-semibold mt-10 mb-4">Bulk and Developer Pricing</h2>
            <p>
              Unit price is only half of a project budget. For developers, hotels and
              architects the variables that move the total are order volume, freight
              route and how much of the bathroom package comes from one supplier.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Volume tiers</strong> quoted directly against your unit count and delivery schedule.</li>
              <li><strong>Air freight</strong> on stocked SKUs in 10–15 days for phased works and mock-up rooms.</li>
              <li><strong>Sea freight</strong> in around 40 days, roughly 240 units per 40-ft container, for full-property rollouts.</li>
              <li><strong>Package supply</strong> — combining smart toilets with bathtubs, whirlpool spas and vanities from the{" "}
                <LocalizedLink to="/bath-sanitary" className="text-primary underline underline-offset-4">
                  Bath &amp; Sanitary collection
                </LocalizedLink>{" "}
                under one agreement.
              </li>
              <li><strong>OEM and white-label</strong> options from defined minimum order quantities.</li>
            </ul>
            <p>
              Full terms and the project enquiry route are on the{" "}
              <LocalizedLink to="/business" className="text-primary underline underline-offset-4">
                B2B &amp; projects page
              </LocalizedLink>
              .
            </p>

            <h2 className="text-2xl font-semibold mt-10 mb-4">Total Cost of Ownership</h2>
            <p>
              Running costs are modest but not zero. Budget for electricity (heated seat
              and instant heating are the main draws), periodic descaling in hard-water
              regions, and occasional consumable replacement. Against that, an integrated
              bidet removes most toilet paper consumption, and water-saving dual flush
              reduces consumption versus an older cistern unit — which is why the payback
              argument is usually strongest at portfolio scale rather than in a single
              bathroom.
            </p>
          </article>

          <FAQSection
            faqs={faqs}
            intro="Common questions about smart toilet pricing in Europe."
            className="mt-12"
          />

          <section className="mt-12 rounded-2xl bg-muted/40 p-8 text-center">
            <h2 className="font-playfair text-2xl font-semibold mb-3">Get a written price for your project</h2>
            <p className="text-muted-foreground mb-6">
              Per-unit or per-container, residential or hospitality — we quote in writing
              with lead times included.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg">
                <LocalizedLink to="/contact">Request a Quote</LocalizedLink>
              </Button>
              <Button asChild size="lg" variant="outline">
                <LocalizedLink to="/business">Developer &amp; Bulk Pricing</LocalizedLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SmartToiletCost;
